/**
 * Clôture des saisies — le filet de rattrapage.
 *
 * La finance pose le verrou depuis l'export du rapport, sans jamais pouvoir
 * le reculer. Cet encart est le seul endroit où un administrateur peut
 * revenir en arrière ou tout rouvrir, après une clôture posée de travers.
 */
import { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isValidMonthKey, monthKey } from '@altyor-kronos/shared-types';
import { useTimeLock, setTimeLock } from '../../features/kronos/useTimeLock';
import { formatMonthKey } from '../../features/kronos/CloseMonthDialog';

export default function TimeLockTab() {
  const { t, i18n } = useTranslation();
  const { lockedThrough, loading } = useTimeLock();
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // `null` tant que l'admin n'a rien tapé : la valeur serveur reste affichée
  // même quand un autre onglet la fait bouger sous ses pieds.
  const value = draft ?? lockedThrough ?? '';
  const dirty = draft !== null && draft !== (lockedThrough ?? '');
  const malformed = value !== '' && !isValidMonthKey(value);
  // Le serveur refuse de clore un mois à venir (#66).
  const future = !malformed && value !== '' && value > monthKey(new Date());

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await setTimeLock(value === '' ? null : value);
      setDraft(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{t('kronos.timeLock.adminTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('kronos.timeLock.adminHelp')}
      </Typography>

      {!loading && (
        <Alert severity={lockedThrough ? 'info' : 'success'} sx={{ mb: 3 }}>
          {lockedThrough
            ? `${t('kronos.timeLock.adminCurrent')} ${formatMonthKey(lockedThrough, i18n.language)}`
            : t('kronos.timeLock.adminNone')}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <TextField
          label={t('kronos.timeLock.adminCurrent')}
          value={value}
          onChange={(e) => setDraft(e.target.value.trim())}
          placeholder="2026-08"
          error={malformed || future}
          helperText={malformed ? t('kronos.timeLock.adminInvalid') : future ? t('kronos.timeLock.adminFuture') : ' '}
          size="small"
          slotProps={{ htmlInput: { 'data-testid': 'time-lock-input' } }}
        />
        <Button
          variant="contained"
          onClick={save}
          disabled={!dirty || malformed || future || saving}
          sx={{ mt: 0.5 }}
        >
          {t('kronos.timeLock.adminSave')}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );
}
